import type { Edge, Node } from "@xyflow/react";

/** Metadata block in published `canvas.json`. */
export type PublicCanvasMeta = {
  id: string;
  name: string;
  og_title?: string;
  description?: string;
  /** Canvas owner display name for the public-view header credit. */
  author_name?: string;
  /** Publishing organization name for the public-view header credit. */
  author_org_name?: string;
  /** Publishing organization id, for derived signup attribution. */
  author_org_id?: string;
};

/**
 * One derived card preview copy under the canvas public prefix.
 * Mirrors backend `canvasPublishedArtifactFilePreview` (Alec's publish shape).
 */
export type PublicCanvasFilePreview = {
  object_key: string;
  width?: number;
  height?: number;
  size?: number;
};

/** One published file copy under the canvas public prefix. */
export type PublicCanvasFile = {
  id: string;
  object_key: string;
  /**
   * Public copies of in-app card previews, keyed by size slot (`"640"`).
   * Object keys live under `public/canvases/{id}/file-previews/{fileId}/…`.
   */
  previews?: Record<string, PublicCanvasFilePreview>;
  /** Opaque digest of source object versions; unused by the SPA. */
  version_fingerprint?: string;
  content_type?: string;
  filename?: string;
  width?: number;
  height?: number;
};

/**
 * Liveblocks `flow` snapshot after chatSession strip — maps keyed by id
 * (same shape React Flow / LiveMap use on the wire).
 */
export type PublicCanvasState = {
  nodes: Record<string, Node>;
  edges: Record<string, Edge>;
};

/** Full published artifact served at `/public/canvases/{id}/canvas.json`. */
export type PublicCanvasArtifact = {
  canvas: PublicCanvasMeta;
  files: Record<string, PublicCanvasFile>;
  state: PublicCanvasState;
};

export function publicFileUrl(
  file: PublicCanvasFile,
  shareOrigin: string,
): string {
  const key = file.object_key.replace(/^\//, "");
  return `${shareOrigin.replace(/\/$/, "")}/${key}`;
}

/**
 * Absolute CDN URL for a published preview object key, or undefined when empty.
 */
export function publicObjectKeyUrl(
  objectKey: string | undefined,
  shareOrigin: string,
): string | undefined {
  const key = objectKey?.replace(/^\//, "");
  if (!key) return undefined;
  return `${shareOrigin.replace(/\/$/, "")}/${key}`;
}

/** URL for the derived 640px card preview JPG, or undefined when not published. */
export function publicFilePreviewUrl(
  file: PublicCanvasFile,
  shareOrigin: string,
): string | undefined {
  return publicObjectKeyUrl(file.previews?.["640"]?.object_key, shareOrigin);
}
